import classnames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from 'react-bootstrap';

import {
  QUICK_IMPORT_SNIPPET_DETECTION,
  QUICK_IMPORT_VENDORED_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../typings/permissions';
import { useIsFeatureEnabled } from '../../../helpers/features';
import useOrganizationQuickImportScanMethodsSettings from '../../../hooks/api/organization/useOrganizationQuickImportScanMethodsSettings';
import { useHasPermission } from '../../../shared/permissions';
import Loader from '../../generic/Loader';
import Toggle from '../../generic/Toggle';
import Tooltip from '../../generic/Tooltip';

import type { ScanMethodSelections } from './types';

import styles from './QuickImportScanMethodsFields.module.less';

type Props = {
  organizationId: number;
  enabled: boolean;
  introClassName?: string;
  onChange: (selections: ScanMethodSelections) => void;
};

const QuickImportScanMethodsFields = (props: Props) => {
  const { organizationId, enabled: isEnabled, introClassName, onChange } = props;
  const { data } = useOrganizationQuickImportScanMethodsSettings(organizationId, isEnabled);
  const hasVendoredFF = useIsFeatureEnabled(QUICK_IMPORT_VENDORED_DETECTION);
  const hasSnippetFF = useIsFeatureEnabled(QUICK_IMPORT_SNIPPET_DETECTION);
  const hasPermission = useHasPermission();
  const canSavePreference = hasPermission({
    resourceType: ResourceType.OrganizationGeneralSettings,
    action: Action.Edit,
  });

  const [isVendored, setIsVendored] = useState(false);
  const [isSnippet, setIsSnippet] = useState(false);
  const [shouldSave, setShouldSave] = useState(false);

  const seededRef = useRef(false);

  const emit = useCallback((vendored: boolean, snippet: boolean, save: boolean) => {
    onChange({
      quickImportVendoredDetectionEnabled: hasVendoredFF && vendored,
      quickImportSnippetDetectionEnabled: hasSnippetFF && snippet,
      quickImportShouldSavePreference: (hasVendoredFF || hasSnippetFF) && save,
    });
  }, [onChange, hasVendoredFF, hasSnippetFF]);

  useEffect(() => {
    if (!data || seededRef.current) {
      return;
    }
    seededRef.current = true;
    const hasVendoredDefault = data.projectDefaultQuickImportVendoredDetectionEnabled ?? false;
    const hasSnippetDefault = data.projectDefaultQuickImportSnippetDetectionEnabled ?? false;
    setIsVendored(hasVendoredDefault);
    setIsSnippet(hasSnippetDefault);
    emit(hasVendoredDefault, hasSnippetDefault, false);
  }, [data, emit]);

  const handleVendoredChange = (value: boolean) => {
    setIsVendored(value);
    emit(value, isSnippet, shouldSave);
  };
  const handleSnippetChange = (value: boolean) => {
    setIsSnippet(value);
    emit(isVendored, value, shouldSave);
  };
  const handleSaveChange = (value: boolean) => {
    setShouldSave(value);
    emit(isVendored, isSnippet, value);
  };

  if (!data) {
    return <Loader />;
  }

  return (
    <>
      <p className={classnames(styles.intro, introClassName)}>
        Include snippets and vendored dependencies to get more complete security and licensing insights.
      </p>
      <Tooltip
        id="quick-import-vendored-tooltip"
        tooltip="Quick Import vendored dependency detection is not enabled for this organization."
        showTooltip={!hasVendoredFF}
        placement="top"
      >
        <div className={styles.toggleRow}>
          <Toggle
            aria-label="Scan for Vendored Dependencies"
            checked={hasVendoredFF && isVendored}
            onChange={handleVendoredChange}
            isDisabled={!hasVendoredFF}
          />
          <span className={classnames(styles.toggleLabel, { [styles.toggleLabelDisabled]: !hasVendoredFF })}>
            Scan for Vendored Dependencies
          </span>
        </div>
      </Tooltip>
      <Tooltip
        id="quick-import-snippet-tooltip"
        tooltip="Quick Import snippet detection is not enabled for this organization."
        showTooltip={!hasSnippetFF}
        placement="top"
      >
        <div className={styles.toggleRow}>
          <Toggle
            aria-label="Scan for Snippets"
            checked={hasSnippetFF && isSnippet}
            onChange={handleSnippetChange}
            isDisabled={!hasSnippetFF}
          />
          <span className={classnames(styles.toggleLabel, { [styles.toggleLabelDisabled]: !hasSnippetFF })}>
            Scan for Snippets
          </span>
        </div>
      </Tooltip>
      <Tooltip
        id="quick-import-save-preference-tooltip"
        tooltip="You don't have permission to update the organization's default quick import scan settings."
        showTooltip={!canSavePreference}
        placement="top"
      >
        <span className={styles.savePreference}>
          <Checkbox
            aria-label="Save this preference for future imports"
            checked={shouldSave}
            onChange={() => handleSaveChange(!shouldSave)}
            disabled={!canSavePreference}
          >
            Save this preference for future imports
          </Checkbox>
        </span>
      </Tooltip>
    </>
  );
};

export default QuickImportScanMethodsFields;
